import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Popover, 
  Badge, 
  IconButton, 
  Typography, 
  Box, 
  Button,
  CircularProgress
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useNotifications } from '../../context/NotificationsContext';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../../theme/brandTheme';

const NotificationsSystem = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    hasMore,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    fetchNotifications,
    websocketConnected,
  } = useNotifications();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const popoverRef = useRef(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const refreshedRef = useRef(false);
  
  const totalUnreadCount = unreadCount;

  // Bildirim listesini açarken bildirimleri yenile
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    // Sadece ilk açılışta fetch yap
    if (!refreshedRef.current) {
      fetchNotifications(1, false);
      refreshedRef.current = true;
    }
  }, [fetchNotifications]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    // Popover kapandığında refreshed flag'i reset et
    setTimeout(() => {
      refreshedRef.current = false;
    }, 500);
  }, []);

  // Popover scroll event for infinite loading
  const handleScroll = useCallback(() => {
    if (!popoverRef.current) return;
    
    const container = popoverRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Load more when user scrolls to the bottom (with some threshold)
    if (scrollHeight - scrollTop - clientHeight < 50 && !loading && hasMore) {
      loadMoreNotifications();
    }
  }, [loading, hasMore, loadMoreNotifications]);
  
  // Set up scroll listener
  useEffect(() => {
    const currentPopover = popoverRef.current;
    if (open && currentPopover) {
      currentPopover.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (currentPopover) {
        currentPopover.removeEventListener('scroll', handleScroll);
      }
    };
  }, [open, handleScroll]);

  // Handle notification click
  const handleNotificationClick = useCallback((notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    
    // Report notifications open the full detail page
    if (notification.resourceType === 'report' && notification.resourceId) {
      navigate(`/dashboard/reports/${notification.resourceId}`);
      handleClose();
      return;
    }
  }, [markAsRead, navigate, handleClose]);
  
  // Get icon based on notification type
  const getNotificationIcon = useCallback((type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ color: BRAND.green }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#F1C40F' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#E74C3C' }} />;
      case 'report':
        return <BarChartIcon sx={{ color: '#3498DB' }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ color: '#3498DB' }} />;
    }
  }, []);
  
  // Format date for display
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) {
      return 'Az önce';
    } else if (diffMin < 60) {
      return `${diffMin} dakika önce`;
    } else if (diffHour < 24) {
      return `${diffHour} saat önce`;
    } else if (diffDay < 7) {
      return `${diffDay} gün önce`;
    } else {
      return date.toLocaleDateString('tr-TR');
    }
  }, []);

  return (
    <>
      <IconButton 
        onClick={handleClick}
        sx={{ 
          color: '#666',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Badge 
          badgeContent={totalUnreadCount} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#f44336',
              color: 'white',
              fontWeight: 'bold'
            }
          }}
        >
          <NotificationsActiveIcon sx={{ color: BRAND.purple }} />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px'
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BRAND.purple }}>
            Bildirimler {totalUnreadCount > 0 && `(${totalUnreadCount})`}
          </Typography>
          
          {totalUnreadCount > 0 && (
            <Button 
              size="small" 
              onClick={() => markAllAsRead()}
              sx={{
                fontSize: '0.75rem',
                color: BRAND.purple,
              }}
            >
              Tümünü Okundu İşaretle
            </Button>
          )}
        </Box>
        
        <Box 
          ref={popoverRef}
          sx={{
            padding: '10px',
            maxHeight: '400px',
            overflowY: 'auto',
            minWidth: '350px'
          }}
          onScroll={handleScroll}
        >
          {notifications.length > 0 ? (
            notifications.map(notification => (
              <Box 
                key={notification._id}
                sx={{
                  padding: '12px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  position: 'relative',
                  backgroundColor: notification.isRead ? 'transparent' : 'rgba(36, 11, 75, 0.04)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  },
                  '&:last-of-type': {
                    borderBottom: 'none'
                  }
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <Box sx={{ mt: 1 }}>
                  {getNotificationIcon(notification.type)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: notification.isRead ? 500 : 600, 
                    color: BRAND.purple,
                    pr: 4 // Space for delete button
                  }}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#666', 
                    display: 'block',
                    fontSize: '0.875rem',
                    mb: 0.5
                  }}>
                    {notification.message}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#888',
                      display: 'block',
                      fontSize: '0.75rem'
                    }}
                  >
                    {formatDate(notification.createdAt)}
                  </Typography>
                </Box>
                
                <IconButton 
                  size="small" 
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    top: '8px',
                    padding: '4px',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 1
                    },
                    '.MuiBox-root:hover &': {
                      opacity: 1
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNotification(notification._id);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" sx={{ color: '#999' }} />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {loading ? 'Bildirimler yükleniyor...' : 'Bildirim bulunmuyor'}
              </Typography>
            </Box>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} color="primary" />
            </Box>
          )}
          
          {!loading && hasMore && (
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Button 
                size="small" 
                onClick={loadMoreNotifications}
                sx={{ fontSize: '0.75rem', color: BRAND.purple }}
              >
                Daha Fazla Yükle
              </Button>
            </Box>
          )}
        </Box>
        
        {websocketConnected ? (
          <Box sx={{ 
            p: 1, 
            borderTop: '1px solid #eee', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: '#4caf50',
                mr: 1
              }} 
            />
            <Typography variant="caption" sx={{ color: '#666' }}>
              Gerçek zamanlı bildirimler aktif
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            p: 1, 
            borderTop: '1px solid #eee', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: '#f44336',
                mr: 1
              }} 
            />
            <Typography variant="caption" sx={{ color: '#666' }}>
              Otomatik yenilemeli mod aktif
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default React.memo(NotificationsSystem); 